/**
 * Mentorship v1 service module — wraps every /api/mentorship/* endpoint.
 *
 * Conventions follow projects.ts exactly:
 *   - Local TypeScript types declared at the top
 *   - All HTTP via apiClient (axios instance with auth + token refresh)
 *   - Functions throw on error (no union return types)
 *   - Paginated responses unwrap to { results, count, next, previous }
 *
 * SSOT: the JSON shapes here MUST match the structured-decision-engine
 * spec verbatim where the spec specifies a shape (Assessment,
 * AssessmentReview, FeedbackTemplate, Report, Insight, ClientProfile, Risk,
 * EngagementSummary, LearningUpdate). MentorshipProject is not in the spec
 * but is required by the workflow — it lives in the same namespace.
 */

import { apiClient } from './apiClient';

const API_BASE = import.meta.env.VITE_API_URL || '';

// ─── Shared enum types ──────────────────────────────────────────────────

export type ModuleKey =
    | 'concept'
    | 'design'
    | 'authorities'
    | 'contract'
    | 'tender'
    | 'construction';

/** Display labels for the six framework modules. SSOT — every component
 * that needs to render a module name in human-readable form imports this
 * constant instead of declaring its own copy. Keep in sync with the backend
 * MODULE_LABELS in education/services.py. */
export const MODULE_LABELS: Record<ModuleKey, string> = {
    concept: 'Concept / Due Diligence',
    design: 'Design',
    authorities: 'Authorities',
    contract: 'Contract',
    tender: 'Tender',
    construction: 'Construction',
};

/** Stable ordering for module-keyed iteration (matches the framework
 * progression Concept → Construction). */
export const MODULE_ORDER: ModuleKey[] = [
    'concept',
    'design',
    'authorities',
    'contract',
    'tender',
    'construction',
];

export type LevelKey = 'Low' | 'Mid' | 'Senior';

export type ConfidenceTier = 'low' | 'medium' | 'high';

export type Priority = 'low' | 'medium' | 'high';

export type SubmissionState = 'submitted' | 'assessed' | 'reviewed' | 'finalized';

export type AssessmentState = 'pending_ai' | 'ready' | 'schema_error';

export type RoleKey = 'mentor' | 'mentee' | 'admin' | null;

export type RoleRequestType = 'become_mentee' | 'become_mentor' | 'change_mentor';

export type RoleRequestState = 'pending' | 'approved' | 'rejected' | 'superseded';

export type InsightType = 'behavioral' | 'technical' | 'risk';

export type InsightSource = 'assessment' | 'mentor' | 'system';

export type ClientProfileVisibility = 'org_wide' | 'mentor_curated';

// ─── Entity shapes ──────────────────────────────────────────────────────

export type UserMini = {
    id: number;
    first_name: string;
    last_name: string;
    email: string;
};

export type Submission = {
    id: string;
    mentee: number;
    mentee_summary: UserMini | null;
    assigned_mentor: number | null;
    module: ModuleKey;
    quarter: string;
    content: string;
    state: SubmissionState;
    is_locked: boolean;
    client_submission_id: string;
    assessment_id: string | null;
    submitted_at: string;
    updated_at: string;
};

export type AssessmentJustification = {
    strengths: string[];
    gaps: string[];
    incorrect_assumptions: string[];
};

export type AssessmentSignals = {
    keywords: string[];
    missing_concepts: string[];
    risk_awareness: string[];
    thinking_patterns: {
        proactive: boolean;
        if_then: boolean;
    };
};

export type Assessment = {
    id: string;
    submission: string;
    module: ModuleKey;
    state: AssessmentState;
    score_ai: number | null;
    confidence: number | null;
    justification: AssessmentJustification;
    signals: AssessmentSignals;
    flags: string[];
    mentor_prompts: string[];
    model_name: string;
    prompt_version: string;
    rubric_version: string;
    error_detail: string;
    latency_ms: number | null;
    token_usage: Record<string, number>;
    created_at: string;
    updated_at: string;
};

export type AssessmentReview = {
    id: string;
    assessment: string;
    suggested_score_range: [number, number];
    level_mapping: LevelKey;
    review_focus: string[];
    confidence: number;
    model_name: string;
    prompt_version: string;
    created_at: string;
    updated_at: string;
};

export type FeedbackTemplate = {
    id: string;
    assessment: string;
    quick_options: string[];
    focus_areas: string[];
    editable: boolean;
    prompt_version: string;
    created_at: string;
    updated_at: string;
};

/** Minimal Submission projection inlined on Report so the mentee's
 * ReportViewer can render "Module, Quarter" context without a second
 * round-trip. Mirrors ``ReportSubmissionSummarySerializer`` on the backend. */
export type ReportSubmissionSummary = {
    id: string;
    module: ModuleKey;
    quarter: string;
    submitted_at: string;
};

export type Report = {
    id: string;
    assessment: string;
    submission: string;
    submission_summary: ReportSubmissionSummary;
    mentee: number;
    version: number;
    final_score: number;
    agreed_level: LevelKey;
    strengths: string[];
    gaps: string[];
    actions: string[];
    mentor_notes: string;
    override_reason: string;
    benchmark_tag: string;
    supersedes: string | null;
    finalized_by: number;
    timestamp: string;
};

export type Insight = {
    id: string;
    user: number | null;
    type: InsightType;
    priority: Priority;
    trigger_context: string;
    prompt: string;
    source: InsightSource;
    source_report: string | null;
    is_active: boolean;
    accepted_count: number;
    dismissed_count: number;
    created_at: string;
    updated_at: string;
};

export type ClientProfile = {
    id: string;
    client_id: string;
    priorities: string[];
    behaviors: string[];
    management_advice: string[];
    visibility: ClientProfileVisibility;
    created_by: number;
    created_at: string;
    updated_at: string;
};

export type MentorshipProject = {
    id: string;
    name: string;
    client_id: string;
    description: string;
    mentee_id: number;
    mentor_id: number | null;
    start_date: string | null;
    end_date: string | null;
    is_active: boolean;
    created_by_id: number;
    created_at: string;
    updated_at: string;
    risks_count: number;
};

export type Risk = {
    id: string;
    project: string;
    trigger: string;
    impact: string;
    mitigation: string;
    priority: Priority;
    is_active: boolean;
    created_by: number;
    created_at: string;
    updated_at: string;
};

export type EngagementSummary = {
    entity: 'EngagementSummary';
    user_id: number;
    period: string;
    top_topics: string[];
    issues: string[];
    improvements: string[];
    effective_prompts: string[];
    ignored_prompts: string[];
    mentor_focus: string;
};

export type EngagementSummarySnapshot = {
    id: string;
    user: number;
    period_start: string;
    period_end: string;
    top_topics: string[];
    issues: string[];
    improvements: string[];
    effective_prompts: string[];
    ignored_prompts: string[];
    mentor_focus: string;
    generated_at: string;
    generated_by: number | null;
};

export type LearningUpdatePattern = {
    pattern: string;
    evidence_count: number;
    sample_user_ids: number[];
};

export type LearningUpdate = {
    id: string;
    patterns_detected: LearningUpdatePattern[];
    rules_updated: string[];
    system_recommendations: string[];
    period_start: string;
    period_end: string;
    generated_at: string;
    generated_by: string;
    applied_at: string | null;
};

export type RoleRequest = {
    id: string;
    user: number;
    request_type: RoleRequestType;
    state: RoleRequestState;
    payload: Record<string, unknown>;
    decided_by: number | null;
    decision_reason: string;
    created_at: string;
    updated_at: string;
};

export type RoleAuditLogRow = {
    id: string;
    actor: number;
    target: number;
    field: string;
    old_value: unknown;
    new_value: unknown;
    reason: string;
    created_at: string;
};

export type Notification = {
    id: string;
    type: string;
    payload: Record<string, unknown>;
    read_at: string | null;
    created_at: string;
};

export type UserSummary = {
    id: number;
    username: string;
    email: string;
    first_name: string;
    last_name: string;
    role: RoleKey;
    mentor_id: number | null;
    mentor_specialties: ModuleKey[];
    mentor_capacity: number | null;
    active_load: number;
};

// ─── Pagination envelope ────────────────────────────────────────────────

export type PagedResponse<T> = {
    count: number;
    next: string | null;
    previous: string | null;
    results: T[];
};

function url(path: string): string {
    return `${API_BASE}${path}`;
}

// ────────────────────────────────────────────────────────────────────────
// Submissions
// ────────────────────────────────────────────────────────────────────────

export async function getSubmissions(
    filters: { state?: SubmissionState; module?: ModuleKey; page?: number } = {},
): Promise<PagedResponse<Submission>> {
    const params = new URLSearchParams();
    if (filters.state) params.set('state', filters.state);
    if (filters.module) params.set('module', filters.module);
    if (filters.page) params.set('page', String(filters.page));
    const qs = params.toString();
    const response = await apiClient.get(url(`/api/mentorship/submissions/${qs ? '?' + qs : ''}`));
    return response.data;
}

export async function createSubmission(
    module: ModuleKey,
    content: string,
    clientSubmissionId: string,
): Promise<Submission> {
    const response = await apiClient.post(url('/api/mentorship/submissions/'), {
        module,
        content,
        client_submission_id: clientSubmissionId,
    });
    return response.data;
}

export async function getSubmission(submissionId: string): Promise<Submission> {
    const response = await apiClient.get(url(`/api/mentorship/submissions/${submissionId}/`));
    return response.data;
}

export async function retryAssessment(submissionId: string): Promise<Assessment> {
    const response = await apiClient.post(
        url(`/api/mentorship/submissions/${submissionId}/retry_assessment/`),
    );
    return response.data;
}

export async function assignSubmissionMentor(
    submissionId: string,
    mentorId: number,
    reason: string,
): Promise<Submission> {
    const response = await apiClient.patch(
        url(`/api/mentorship/submissions/${submissionId}/assign/`),
        { mentor_id: mentorId, reason },
    );
    return response.data;
}

// ────────────────────────────────────────────────────────────────────────
// Assessment / AssessmentReview / FeedbackTemplate
// ────────────────────────────────────────────────────────────────────────

export async function getAssessment(assessmentId: string): Promise<Assessment> {
    const response = await apiClient.get(url(`/api/mentorship/assessments/${assessmentId}/`));
    return response.data;
}

export async function getAssessmentReview(assessmentId: string): Promise<AssessmentReview> {
    const response = await apiClient.get(
        url(`/api/mentorship/assessment_reviews/${assessmentId}/`),
    );
    return response.data;
}

export async function regenerateAssessmentReview(
    assessmentId: string,
): Promise<AssessmentReview> {
    const response = await apiClient.post(
        url(`/api/mentorship/assessment_reviews/${assessmentId}/regenerate/`),
    );
    return response.data;
}

export async function getFeedbackTemplate(assessmentId: string): Promise<FeedbackTemplate> {
    const response = await apiClient.get(
        url(`/api/mentorship/feedback_templates/${assessmentId}/`),
    );
    return response.data;
}

export async function regenerateFeedbackTemplate(
    assessmentId: string,
): Promise<FeedbackTemplate> {
    const response = await apiClient.post(
        url(`/api/mentorship/feedback_templates/${assessmentId}/regenerate/`),
    );
    return response.data;
}

// ────────────────────────────────────────────────────────────────────────
// Reports
// ────────────────────────────────────────────────────────────────────────

export type CreateReportInput = {
    assessment_id: string;
    final_score: number;
    agreed_level: LevelKey;
    strengths: string[];
    gaps: string[];
    actions: string[];
    mentor_notes?: string;
    override_reason?: string;
    benchmark_tag?: string;
};

export async function createReport(input: CreateReportInput): Promise<Report> {
    const response = await apiClient.post(url('/api/mentorship/reports/'), input);
    return response.data;
}

export async function listReports(
    filters: { mentee_id?: number; module?: ModuleKey; page?: number } = {},
): Promise<PagedResponse<Report>> {
    const params = new URLSearchParams();
    if (filters.mentee_id) params.set('mentee_id', String(filters.mentee_id));
    if (filters.module) params.set('module', filters.module);
    if (filters.page) params.set('page', String(filters.page));
    const qs = params.toString();
    const response = await apiClient.get(url(`/api/mentorship/reports/${qs ? '?' + qs : ''}`));
    return response.data;
}

export async function getReport(reportId: string): Promise<Report> {
    const response = await apiClient.get(url(`/api/mentorship/reports/${reportId}/`));
    return response.data;
}

// ────────────────────────────────────────────────────────────────────────
// Insights
// ────────────────────────────────────────────────────────────────────────

export type CreateInsightInput = {
    user_id: number;
    type: InsightType;
    priority: Priority;
    trigger_context: string;
    prompt: string;
};

export async function listInsights(
    filters: { user_id?: number; type?: InsightType; priority?: Priority; page?: number } = {},
): Promise<PagedResponse<Insight>> {
    const params = new URLSearchParams();
    if (filters.user_id) params.set('user_id', String(filters.user_id));
    if (filters.type) params.set('type', filters.type);
    if (filters.priority) params.set('priority', filters.priority);
    if (filters.page) params.set('page', String(filters.page));
    const qs = params.toString();
    const response = await apiClient.get(url(`/api/mentorship/insights/${qs ? '?' + qs : ''}`));
    return response.data;
}

export async function createInsight(input: CreateInsightInput): Promise<Insight> {
    const response = await apiClient.post(url('/api/mentorship/insights/'), input);
    return response.data;
}

export async function updateInsight(
    insightId: string,
    partial: Partial<Pick<Insight, 'type' | 'priority' | 'trigger_context' | 'prompt'>>,
): Promise<Insight> {
    const response = await apiClient.patch(url(`/api/mentorship/insights/${insightId}/`), partial);
    return response.data;
}

export async function archiveInsight(insightId: string): Promise<void> {
    await apiClient.delete(url(`/api/mentorship/insights/${insightId}/`));
}

export async function recordInsightFeedback(
    insightId: string,
    action: 'accepted' | 'dismissed',
): Promise<Insight> {
    const response = await apiClient.post(
        url(`/api/mentorship/insights/${insightId}/feedback/`),
        { action },
    );
    return response.data;
}

// ────────────────────────────────────────────────────────────────────────
// Client profiles
// ────────────────────────────────────────────────────────────────────────

export type UpsertClientProfileInput = {
    client_id: string;
    priorities: string[];
    behaviors: string[];
    management_advice: string[];
    visibility?: ClientProfileVisibility;
};

export async function listClientProfiles(
    filters: { client_id?: string; page?: number } = {},
): Promise<PagedResponse<ClientProfile>> {
    const params = new URLSearchParams();
    if (filters.client_id) params.set('client_id', filters.client_id);
    if (filters.page) params.set('page', String(filters.page));
    const qs = params.toString();
    const response = await apiClient.get(
        url(`/api/mentorship/client_profiles/${qs ? '?' + qs : ''}`),
    );
    return response.data;
}

export async function upsertClientProfile(
    input: UpsertClientProfileInput,
): Promise<ClientProfile> {
    const response = await apiClient.post(url('/api/mentorship/client_profiles/'), input);
    return response.data;
}

export async function getClientProfile(profileId: string): Promise<ClientProfile> {
    const response = await apiClient.get(url(`/api/mentorship/client_profiles/${profileId}/`));
    return response.data;
}

export async function updateClientProfile(
    profileId: string,
    partial: Partial<UpsertClientProfileInput>,
): Promise<ClientProfile> {
    const response = await apiClient.patch(
        url(`/api/mentorship/client_profiles/${profileId}/`),
        partial,
    );
    return response.data;
}

export async function deleteClientProfile(profileId: string): Promise<void> {
    await apiClient.delete(url(`/api/mentorship/client_profiles/${profileId}/`));
}

// ────────────────────────────────────────────────────────────────────────
// MentorshipProject
// ────────────────────────────────────────────────────────────────────────

export type CreateMentorshipProjectInput = {
    name: string;
    mentee_id: number;
    mentor_id?: number | null;
    client_id?: string;
    description?: string;
    start_date?: string | null;
    end_date?: string | null;
};

export type UpdateMentorshipProjectInput = Partial<
    Pick<
        MentorshipProject,
        'name' | 'client_id' | 'description' | 'start_date' | 'end_date'
    >
>;

export async function listMentorshipProjects(
    filters: {
        mentee_id?: number;
        mentor_id?: number;
        client_id?: string;
        active?: boolean;
        page?: number;
    } = {},
): Promise<PagedResponse<MentorshipProject>> {
    const params = new URLSearchParams();
    if (filters.mentee_id) params.set('mentee_id', String(filters.mentee_id));
    if (filters.mentor_id) params.set('mentor_id', String(filters.mentor_id));
    if (filters.client_id) params.set('client_id', filters.client_id);
    if (filters.active === false) params.set('active', '0');
    if (filters.page) params.set('page', String(filters.page));
    const qs = params.toString();
    const response = await apiClient.get(
        url(`/api/mentorship/projects/${qs ? '?' + qs : ''}`),
    );
    return response.data;
}

export async function createMentorshipProject(
    input: CreateMentorshipProjectInput,
): Promise<MentorshipProject> {
    const response = await apiClient.post(url('/api/mentorship/projects/'), input);
    return response.data;
}

export async function getMentorshipProject(projectId: string): Promise<MentorshipProject> {
    const response = await apiClient.get(url(`/api/mentorship/projects/${projectId}/`));
    return response.data;
}

export async function updateMentorshipProject(
    projectId: string,
    partial: UpdateMentorshipProjectInput,
): Promise<MentorshipProject> {
    const response = await apiClient.patch(
        url(`/api/mentorship/projects/${projectId}/`),
        partial,
    );
    return response.data;
}

export async function archiveMentorshipProject(
    projectId: string,
    reason: string = '',
): Promise<MentorshipProject> {
    const response = await apiClient.post(
        url(`/api/mentorship/projects/${projectId}/archive/`),
        { reason },
    );
    return response.data;
}

export async function unarchiveMentorshipProject(
    projectId: string,
    reason: string = '',
): Promise<MentorshipProject> {
    const response = await apiClient.post(
        url(`/api/mentorship/projects/${projectId}/unarchive/`),
        { reason },
    );
    return response.data;
}

export async function reassignMentorshipProjectMentor(
    projectId: string,
    newMentorId: number | null,
    reason: string,
): Promise<MentorshipProject> {
    const response = await apiClient.patch(
        url(`/api/mentorship/projects/${projectId}/reassign_mentor/`),
        { mentor_id: newMentorId, reason },
    );
    return response.data;
}

export async function deleteMentorshipProject(projectId: string): Promise<void> {
    await apiClient.delete(url(`/api/mentorship/projects/${projectId}/`));
}

export async function getMentorshipProjectRisks(projectId: string): Promise<Risk[]> {
    const response = await apiClient.get(
        url(`/api/mentorship/projects/${projectId}/risks/`),
    );
    return response.data;
}

// ────────────────────────────────────────────────────────────────────────
// Risks
// ────────────────────────────────────────────────────────────────────────

export type UpsertRiskInput = {
    project_id: string;
    trigger: string;
    impact: string;
    mitigation: string;
    priority: Priority;
};

export async function listRisks(
    projectId: string,
    filters: { active?: boolean; priority?: Priority; page?: number } = {},
): Promise<PagedResponse<Risk>> {
    const params = new URLSearchParams();
    params.set('project_id', projectId);
    if (filters.active === false) params.set('active', '0');
    if (filters.priority) params.set('priority', filters.priority);
    if (filters.page) params.set('page', String(filters.page));
    const response = await apiClient.get(
        url(`/api/mentorship/risks/?${params.toString()}`),
    );
    return response.data;
}

export async function createRisk(input: UpsertRiskInput): Promise<Risk> {
    const response = await apiClient.post(url('/api/mentorship/risks/'), input);
    return response.data;
}

export async function getRisk(riskId: string): Promise<Risk> {
    const response = await apiClient.get(url(`/api/mentorship/risks/${riskId}/`));
    return response.data;
}

export async function updateRisk(
    riskId: string,
    partial: Partial<Omit<UpsertRiskInput, 'project_id'>>,
): Promise<Risk> {
    const response = await apiClient.patch(url(`/api/mentorship/risks/${riskId}/`), partial);
    return response.data;
}

export async function archiveRisk(riskId: string): Promise<void> {
    await apiClient.delete(url(`/api/mentorship/risks/${riskId}/`));
}

// ────────────────────────────────────────────────────────────────────────
// Engagement summaries (entity #8)
// ────────────────────────────────────────────────────────────────────────

export async function getEngagementSummary(
    userId: number,
    periodStart: string,
    periodEnd: string,
    persist: boolean = false,
): Promise<EngagementSummary | EngagementSummarySnapshot> {
    const params = new URLSearchParams({
        user_id: String(userId),
        period_start: periodStart,
        period_end: periodEnd,
    });
    if (persist) params.set('persist', '1');
    const response = await apiClient.get(
        url(`/api/mentorship/engagement_summaries/?${params.toString()}`),
    );
    return response.data;
}

export async function listEngagementSummarySnapshots(
    userId: number,
): Promise<PagedResponse<EngagementSummarySnapshot>> {
    const response = await apiClient.get(
        url(`/api/mentorship/engagement_summaries/snapshots/?user_id=${userId}`),
    );
    return response.data;
}

// ────────────────────────────────────────────────────────────────────────
// Learning updates (entity #9)
// ────────────────────────────────────────────────────────────────────────

export async function listLearningUpdates(
    page: number = 1,
): Promise<PagedResponse<LearningUpdate>> {
    const response = await apiClient.get(
        url(`/api/mentorship/learning_updates/?page=${page}`),
    );
    return response.data;
}

export async function runLearningUpdate(
    periodStart?: string,
    periodEnd?: string,
): Promise<LearningUpdate> {
    const body: Record<string, string> = {};
    if (periodStart) body.period_start = periodStart;
    if (periodEnd) body.period_end = periodEnd;
    const response = await apiClient.post(url('/api/mentorship/learning_updates/run/'), body);
    return response.data;
}

export async function applyLearningUpdate(updateId: string): Promise<LearningUpdate> {
    const response = await apiClient.post(
        url(`/api/mentorship/learning_updates/${updateId}/apply/`),
    );
    return response.data;
}

// ────────────────────────────────────────────────────────────────────────
// Notifications: see `notificationsService.ts`
// ────────────────────────────────────────────────────────────────────────
//
// Notification helpers live in `src/notificationsService.ts` (single
// SSOT for both mentorship and networking). Import from there:
//
//   import { listNotifications, markNotificationRead,
//            markAllNotificationsRead, getUnreadSummary }
//   from './notificationsService';

// ────────────────────────────────────────────────────────────────────────
// Role management
// ────────────────────────────────────────────────────────────────────────

export type CreateRoleRequestPayload = {
    target_modules?: ModuleKey[];
    specialties?: ModuleKey[];
    seniority?: 'Junior' | 'Mid' | 'Senior';
    bio?: string;
    linkedin?: string;
    desired_mentor_id?: number;
    reason?: string;
};

export async function createRoleRequest(
    requestType: RoleRequestType,
    payload: CreateRoleRequestPayload,
): Promise<RoleRequest> {
    const response = await apiClient.post(url('/api/mentorship/role_requests/'), {
        request_type: requestType,
        payload,
    });
    return response.data;
}

export async function listRoleRequests(
    state?: RoleRequestState,
): Promise<PagedResponse<RoleRequest>> {
    const params = state ? `?state=${state}` : '';
    const response = await apiClient.get(url(`/api/mentorship/role_requests/${params}`));
    return response.data;
}

export async function approveRoleRequest(
    requestId: string,
    reason: string = '',
): Promise<RoleRequest> {
    const response = await apiClient.post(
        url(`/api/mentorship/role_requests/${requestId}/approve/`),
        { reason },
    );
    return response.data;
}

export async function rejectRoleRequest(
    requestId: string,
    reason: string,
): Promise<RoleRequest> {
    const response = await apiClient.post(
        url(`/api/mentorship/role_requests/${requestId}/reject/`),
        { reason },
    );
    return response.data;
}

export type SetUserRoleInput = {
    new_role: 'mentor' | 'mentee' | null;
    reason: string;
    mentor_id?: number | null;
    mentor_specialties?: ModuleKey[];
    mentor_capacity?: number;
};

export async function listUsers(
    filters: { role?: RoleKey; search?: string; page?: number } = {},
): Promise<PagedResponse<UserSummary>> {
    const params = new URLSearchParams();
    if (filters.role) params.set('role', filters.role);
    if (filters.search) params.set('search', filters.search);
    if (filters.page) params.set('page', String(filters.page));
    const qs = params.toString();
    const response = await apiClient.get(url(`/api/mentorship/users/${qs ? '?' + qs : ''}`));
    return response.data;
}

export async function setUserRole(
    userId: number,
    input: SetUserRoleInput,
): Promise<{
    role: RoleKey;
    mentor_id: number | null;
    mentor_specialties: ModuleKey[];
    mentor_capacity: number;
}> {
    const response = await apiClient.patch(
        url(`/api/mentorship/users/${userId}/role/`),
        input,
    );
    return response.data;
}

export async function setUserMentor(
    userId: number,
    mentorId: number | null,
    reason: string,
): Promise<{ mentor_id: number | null }> {
    const response = await apiClient.patch(url(`/api/mentorship/users/${userId}/mentor/`), {
        mentor_id: mentorId,
        reason,
    });
    return response.data;
}

export async function setUserCapacity(
    userId: number,
    mentorCapacity: number,
    reason: string,
): Promise<{ mentor_capacity: number }> {
    const response = await apiClient.patch(
        url(`/api/mentorship/users/${userId}/capacity/`),
        { mentor_capacity: mentorCapacity, reason },
    );
    return response.data;
}

export async function setUserSpecialties(
    userId: number,
    specialties: ModuleKey[],
    reason: string,
): Promise<{ mentor_specialties: ModuleKey[] }> {
    const response = await apiClient.patch(
        url(`/api/mentorship/users/${userId}/specialties/`),
        { mentor_specialties: specialties, reason },
    );
    return response.data;
}

export async function getUserAuditLog(
    userId: number,
    limit: number = 50,
): Promise<RoleAuditLogRow[]> {
    const response = await apiClient.get(
        url(`/api/mentorship/users/${userId}/audit_log/?limit=${limit}`),
    );
    return response.data;
}
